import React, { useEffect, useRef, useState } from "react";

import ClozeSentence from "./ClozeSentence";
import CollectionSelector from "./CollectionSelector";
import ExplanationThread from "./ExplanationThread";
import Markdown from "./Markdown";
import ReportSentence from "./GrammarExercise/ReportSentence";
import { splitTextOnCloze } from "../helpers";

const SETTINGS_KEY = "grammarExerciseSettings";
const DEFAULT_SETTINGS = {
  colorHints: true,
  autoPlayAudio: true,
  voiceName: null,
  translationPosition: "after" // "before" | "after" — relative to the sentence
};

const parseCloze = (text) => {
  const { preClozeStr, cloze, postClozeStr } = splitTextOnCloze(text);
  return { before: preClozeStr, answer: cloze, after: postClozeStr };
};

const fullText = (text) => text.replace(/\{\{(.+?)\}\}/, "$1");

const normalize = (s) => s
  .trim()
  .toLowerCase()
  .replace(/\s+/g, " ")
  .replace(/[‘’ʼ]/g, "'");

// Curated sentences have no generationId and form the first batch; each "practice more"
// call adds a batch with its own completion panel.
const newItem = (sentence) => {
  const progress = sentence.progress || {};
  return {
    id: sentence.id,
    sentence,
    batch: sentence.generationId || 0,
    status: progress.numPlayed > 0 ? "answered" : "pending",
    input: "",
    wrong: false, // transient: last check was wrong, cleared on edit
    wasIncorrect: (progress.numIncorrect || 0) > 0,
    markedHard: !!progress.markedHard,
    given: progress.lastAnswer || null
  };
};

const loadSettings = () => {
  try {
    return { ...DEFAULT_SETTINGS, ...JSON.parse(localStorage.getItem(SETTINGS_KEY) || "{}") };
  } catch (e) {
    return { ...DEFAULT_SETTINGS };
  }
};

// Self-contained browser TTS — intentionally independent of the legacy
// window.clozemaster.getSystemTtsVoices/playSystemUtterance helpers.
// Prefer premium/enhanced/neural voices, then remote (network) voices, then the rest.
const voiceQualityScore = (voice) => {
  let score = 0;
  if (/premium|enhanced|neural|natural|siri/i.test(voice.name)) score += 4;
  if (voice.localService === false) score += 2;
  return score;
};

const getVoices = (iso) => {
  if (!window.speechSynthesis || !iso) return [];
  // Voice langs are BCP-47 ("fr-FR", "fr-CA") and the iso is the bare subtag ("fr").
  // Compare on the prefix rather than building a regex out of a value we don't control.
  const prefix = String(iso).toLowerCase();
  return window.speechSynthesis.getVoices()
    .filter((v) => String(v.lang).toLowerCase().replace("_", "-").split("-")[0] === prefix)
    .sort((a, b) => voiceQualityScore(b) - voiceQualityScore(a));
};

const speak = (text, voice) => {
  const utterance = new SpeechSynthesisUtterance(text);
  utterance.voice = voice;
  utterance.lang = voice.lang;
  window.speechSynthesis.cancel();
  window.speechSynthesis.speak(utterance);
};

export default function GrammarExercise(props) {
  const {
    answerUrl, backUrl, collectionsUrl, exercise, explainUrl, explanationThreadsUrl, gameSettingsUrl,
    generationQuota, generateUrl, isPro, isSignedIn, markHardUrl, nextExercise,
    reportCategories, reportUrl, sentences, targetLanguageIso
  } = props;

  const [items, setItems] = useState(() => sentences.map(newItem));
  const [settings, setSettings] = useState(loadSettings);
  const [showSettings, setShowSettings] = useState(false);
  // Open on a first visit — the lesson is the point. Collapsed once you've answered
  // something here before, so returning to practise isn't a scroll past the rules.
  const [showLesson, setShowLesson] = useState(
    () => !sentences.some((s) => (s.progress || {}).numPlayed > 0)
  );
  const [voices, setVoices] = useState(() => getVoices(targetLanguageIso));
  const [generating, setGenerating] = useState(false);
  const [generateError, setGenerateError] = useState(null);
  // The learner's AI checks used this month. Shared with every other feature on AI
  // checks, so it's taken from each response rather than counted here - a local count
  // would miss checks spent in another tab or feature.
  const [generationsUsed, setGenerationsUsed] = useState(
    () => (generationQuota || {}).countThisMonth || 0
  );
  const syncAiChecks = (aiChecks) => {
    if (aiChecks) setGenerationsUsed(aiChecks.countThisMonth);
  };
  const [addOpenForBatch, setAddOpenForBatch] = useState(null);
  const [selectedForCollection, setSelectedForCollection] = useState(new Set());
  const [selectedCollection, setSelectedCollection] = useState(null);
  const [adding, setAdding] = useState(false);
  const [addedByBatch, setAddedByBatch] = useState({});
  const [addedIds, setAddedIds] = useState(new Set());
  const [shownExplanations, setShownExplanations] = useState(new Set());
  // Explanations already stored on the sentence arrive as props; anything generated on
  // demand lands here so it survives a toggle without re-requesting.
  const [explanations, setExplanations] = useState(() => sentences.reduce((acc, s) => {
    if (s.explanation) acc[s.id] = s.explanation;
    return acc;
  }, {}));
  const [explainingIds, setExplainingIds] = useState(new Set());
  // Follow-up conversations, keyed by sentence id. Owned here rather than inside
  // ExplanationThread so hiding the explanation (which unmounts it) doesn't lose them.
  const [threads, setThreads] = useState(() => sentences.reduce((acc, s) => {
    if (s.thread) acc[s.id] = s.thread;
    return acc;
  }, {}));
  const [lessonThread, setLessonThread] = useState(exercise.lessonThread || null);
  // Dark mode is an account-level game setting, not part of the localStorage blob below —
  // same source of truth as the dashboard and play screens.
  const [darkMode, setDarkMode] = useState(() => $("body").hasClass("dark-mode"));
  const inputRefs = useRef({});
  const containerRef = useRef(null);

  // Delegated so it covers sentences added later by "practice more", and scoped to this
  // component rather than body so it doesn't collide with the dashboard's initializer.
  // container: "body" keeps the tooltip out of the scrolling/overflow containers.
  useEffect(() => {
    const container = containerRef.current;
    if (!container || !window.$ || !$.fn.tooltip) return;

    $(container).tooltip({ selector: "[data-toggle='tooltip']", container: "body" });
    return () => $(container).tooltip("destroy");
  }, []);

  useEffect(() => {
    if (!window.speechSynthesis) return;

    const update = () => setVoices(getVoices(targetLanguageIso));
    window.speechSynthesis.addEventListener("voiceschanged", update);

    // Chrome loads voices asynchronously and fires voiceschanged once. That can happen
    // between the initial render and this effect, in which case the listener above is
    // attached too late and never fires — so read them again now. Safari sometimes never
    // fires the event at all, hence the short retries.
    update();
    const retries = [200, 750, 2000].map((ms) => setTimeout(update, ms));

    return () => {
      window.speechSynthesis.removeEventListener("voiceschanged", update);
      retries.forEach(clearTimeout);
    };
  }, [targetLanguageIso]);

  const updateSettings = (update) => {
    const next = { ...settings, ...update };
    setSettings(next);
    try {
      localStorage.setItem(SETTINGS_KEY, JSON.stringify(next));
    } catch (e) {
      // localStorage unavailable (private browsing) — settings just won't persist
    }
  };

  const toggleDarkMode = (enabled) => {
    setDarkMode(enabled);
    $("body").toggleClass("dark-mode", enabled);
    $.ajax({
      data: { settings: { theme: enabled ? "dark" : "light" } },
      method: "post",
      url: gameSettingsUrl
    });
  };

  const currentVoice = () => {
    if (!voices.length) return null;
    return voices.find((v) => v.name === settings.voiceName) || voices[0];
  };

  const playAudio = (sentence) => {
    const voice = currentVoice();
    if (voice) speak(fullText(sentence.text), voice);
  };

  const updateItem = (i, update) => {
    setItems((prev) => prev.map((item, j) => (j === i ? { ...item, ...update } : item)));
  };

  const focusNextPending = (after) => {
    setTimeout(() => {
      const order = items.map((_, j) => (after + 1 + j) % items.length);
      const next = order.find((j) => j !== after && items[j].status === "pending");
      if (next !== undefined && inputRefs.current[next]) inputRefs.current[next].focus();
    }, 0);
  };

  const persistAnswer = (item, correct) => {
    if (!isSignedIn) return;
    $.ajax({
      method: "post",
      url: answerUrl,
      data: {
        grammar_exercise_cloze_sentence_id: item.id,
        correct: correct,
        answer: item.input
      }
    });
  };

  const checkItem = (i, e) => {
    e.preventDefault();
    const item = items[i];
    if (item.status === "answered") return;
    // second enter on an unchanged wrong answer reveals it
    if (item.wrong) return revealItem(i);
    const { answer } = parseCloze(item.sentence.text);
    if (normalize(item.input) === normalize(answer)) {
      updateItem(i, { status: "answered", given: item.input, wrong: false });
      persistAnswer(item, true);
      if (settings.autoPlayAudio) playAudio(item.sentence);
      focusNextPending(i);
    } else {
      updateItem(i, { wasIncorrect: true, wrong: true, given: item.input });
      persistAnswer(item, false);
    }
  };

  const revealItem = (i) => {
    const item = items[i];
    updateItem(i, { status: "answered", wasIncorrect: true, wrong: false });
    if (!item.wasIncorrect) persistAnswer(item, false);
    if (settings.autoPlayAudio) playAudio(item.sentence);
    focusNextPending(i);
  };

  const toggleHard = (i, markedHard) => {
    updateItem(i, { markedHard });
    if (!isSignedIn) return;
    $.ajax({
      method: "post",
      url: markHardUrl,
      data: { grammar_exercise_cloze_sentence_id: items[i].id, marked_hard: markedHard }
    });
  };

  const showExplanation = (id, shown) => {
    setShownExplanations((prev) => {
      const next = new Set(prev);
      if (shown) next.add(id); else next.delete(id);
      return next;
    });
  };

  const setExplaining = (id, explaining) => {
    setExplainingIds((prev) => {
      const next = new Set(prev);
      if (explaining) next.add(id); else next.delete(id);
      return next;
    });
  };

  const toggleExplanation = (i) => {
    const id = items[i].id;

    if (shownExplanations.has(id)) return showExplanation(id, false);
    if (explanations[id]) return showExplanation(id, true);
    if (explainingIds.has(id)) return;

    setExplaining(id, true);
    $.ajax({
      method: "post",
      url: explainUrl,
      data: { grammar_exercise_cloze_sentence_id: id }
    })
      .done((data) => {
        setExplanations((prev) => ({ ...prev, [id]: data.explanation }));
        syncAiChecks(data.aiChecks);
        showExplanation(id, true);
      })
      .fail((xhr) => {
        const json = xhr.responseJSON || {};
        syncAiChecks(json.aiChecks);
        alert(json.errors ? json.errors[0] : "Couldn't generate an explanation. Please try again.");
      })
      .always(() => setExplaining(id, false));
  };

  const answered = items.filter((item) => item.status === "answered");
  const lastBatch = items.length ? items[items.length - 1].batch : 0;

  // [{ batch, indices, complete, flaggedIndices }] in batch order
  const batches = items.reduce((acc, item, i) => {
    let batch = acc[acc.length - 1];
    if (!batch || batch.batch !== item.batch) {
      batch = { batch: item.batch, indices: [], complete: true, flaggedIndices: [] };
      acc.push(batch);
    }
    batch.indices.push(i);
    if (item.status !== "answered") batch.complete = false;
    else if (item.markedHard || item.wasIncorrect) batch.flaggedIndices.push(i);
    return acc;
  }, []);

  // Any answered sentence can be added to a collection — the hard/missed ones are
  // just pre-checked because they're the likeliest picks.
  const openAddToCollection = (batch) => {
    setSelectedForCollection(new Set(
      batch.flaggedIndices.filter((i) => !addedIds.has(items[i].id))
    ));
    setAddOpenForBatch(batch.batch);
  };

  const toggleForCollection = (i) => {
    setSelectedForCollection((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i); else next.add(i);
      return next;
    });
  };

  const generateMore = () => {
    setGenerating(true);
    setGenerateError(null);
    $.ajax({ method: "post", url: generateUrl })
      .done((data) => {
        setItems((prev) => prev.concat(data.sentences.map(newItem)));
        syncAiChecks(data.aiChecks);
      })
      .fail((xhr) => {
        const json = xhr.responseJSON || {};
        syncAiChecks(json.aiChecks);
        setGenerateError(json.errors ? json.errors[0] : "Oh no! There was an error generating sentences. Please try again.");
      })
      .always(() => setGenerating(false));
  };

  const addToCollection = (batch) => {
    if (!selectedCollection) return;
    setAdding(true);
    const addedIdxs = Array.from(selectedForCollection);
    const posts = addedIdxs.map((i) => () => $.ajax({
      method: "post",
      url: selectedCollection.collectionClozeSentencesUrl,
      data: {
        collection_cloze_sentence: {
          text: items[i].sentence.text,
          translation: items[i].sentence.translation,
          hint: items[i].sentence.hint
        }
      }
    }));
    // POST sequentially to keep things simple and avoid hammering the server
    posts.reduce((chain, post) => chain.then(post), Promise.resolve())
      .then(() => {
        setAddedIds((prev) => {
          const next = new Set(prev);
          addedIdxs.forEach((i) => next.add(items[i].id));
          return next;
        });
        setAddedByBatch((prev) => ({
          ...prev,
          [batch.batch]: { count: addedIdxs.length, collectionName: selectedCollection.name }
        }));
        setAddOpenForBatch(null); // back to the list so more can be added later
      })
      .catch(() => alert("Oh no! There was an error adding sentences. Sorry about that. Please try again."))
      .finally(() => setAdding(false));
  };

  const renderSettings = () => (
    <div className="panel panel-default">
      <div className="panel-body">
        <div className="checkbox" style={{ marginTop: 0 }}>
          <label>
            <input
              type="checkbox"
              checked={darkMode}
              onChange={(e) => toggleDarkMode(e.target.checked)}
            />
            {" "}Dark mode
          </label>
        </div>
        <div className="checkbox">
          <label>
            <input
              type="checkbox"
              checked={settings.colorHints}
              onChange={(e) => updateSettings({ colorHints: e.target.checked })}
            />
            {" "}Color hints while typing (green = correct so far, red = off track)
          </label>
        </div>
        <div className="checkbox">
          <label>
            <input
              type="checkbox"
              checked={settings.autoPlayAudio}
              onChange={(e) => updateSettings({ autoPlayAudio: e.target.checked })}
              disabled={!voices.length}
            />
            {" "}Auto-play audio after answering
          </label>
        </div>
        <div className="form-group">
          <label htmlFor="grammar-exercise-translation-position">Translation</label>
          <select
            id="grammar-exercise-translation-position"
            className="form-control"
            value={settings.translationPosition}
            onChange={(e) => updateSettings({ translationPosition: e.target.value })}
          >
            <option value="before">Before the sentence</option>
            <option value="after">After the sentence</option>
          </select>
        </div>
        <div className="form-group" style={{ marginBottom: 0 }}>
          <label htmlFor="grammar-exercise-voice">Voice</label>
          {voices.length ? (
            <select
              id="grammar-exercise-voice"
              className="form-control"
              value={(currentVoice() || {}).name || ""}
              onChange={(e) => updateSettings({ voiceName: e.target.value })}
            >
              {voices.map((v) => (
                <option key={v.name} value={v.name}>{v.name} ({v.lang})</option>
              ))}
            </select>
          ) : (
            <p className="text-muted" style={{ marginBottom: 0 }}>No text-to-speech voices found in this browser.</p>
          )}
        </div>
      </div>
    </div>
  );

  const renderItem = (item, i) => {
    const { before, answer, after } = parseCloze(item.sentence.text);
    const isAnswered = item.status === "answered";
    const showHint = settings.colorHints && !isAnswered && !!item.input.trim();
    const correctSoFar = showHint && normalize(answer).indexOf(normalize(item.input)) === 0;

    const translation = item.sentence.translation;

    return (
      <div key={item.id} className="sentence">
        <form onSubmit={(e) => checkItem(i, e)}>
          {translation && settings.translationPosition === "before" && (
            <p className="text-muted translation-before">{translation}</p>
          )}
          <p className="sentence-text">
            <span className="text-muted sentence-number">{i + 1}.</span>
            {before}
            {isAnswered ? (
              <span className={item.wasIncorrect ? "" : "text-success"}>
                <strong><u>{answer}</u></strong>
                {!item.wasIncorrect && <span className="glyphicon glyphicon-ok revealed-answer" />}
              </span>
            ) : (
              <span className={`answer-input${showHint ? ` with-hint ${correctSoFar ? "correct" : "incorrect"}` : ""}`}>
                <input
                  ref={(el) => { inputRefs.current[i] = el; }}
                  type="text"
                  value={item.input}
                  onChange={(e) => updateItem(i, { input: e.target.value, wrong: false })}
                  autoCapitalize="off"
                  autoComplete="off"
                  autoCorrect="off"
                  spellCheck="false"
                  className="form-control"
                  style={{ width: Math.max(140, answer.length * 14) + (showHint ? 28 : 0) }}
                />
                {showHint && (
                  <span
                    className={`glyphicon glyphicon-${correctSoFar ? "ok" : "remove"} ${correctSoFar ? "text-success" : "text-danger"} hint-icon`}
                  />
                )}
              </span>
            )}
            {after}
            {isAnswered && voices.length > 0 && (
              <button
                type="button"
                className="btn btn-link btn-sm"
                data-toggle="tooltip"
                data-placement="top"
                title="Play audio"
                onClick={() => playAudio(item.sentence)}
              >
                <span className="glyphicon glyphicon-volume-up" />
              </button>
            )}
          </p>

          {(item.sentence.hint || (translation && settings.translationPosition === "after")) && (
            <p className="text-muted" style={{ marginBottom: 5 }}>
              {item.sentence.hint && <span>Hint: {item.sentence.hint}</span>}
              {item.sentence.hint && translation && settings.translationPosition === "after" && " · "}
              {settings.translationPosition === "after" && translation}
            </p>
          )}

          {!isAnswered && (
            <p style={{ marginBottom: 5 }}>
              {item.wrong && <span className="text-muted">Not quite — try again, or </span>}
              <button type="button" className="btn btn-link" style={{ padding: 0 }} onClick={() => revealItem(i)}>
                Show the answer
              </button>
            </p>
          )}

          {isAnswered && (
            <div className="checkbox" style={{ margin: 0 }}>
              <label className="text-muted">
                <input
                  type="checkbox"
                  checked={item.markedHard}
                  onChange={(e) => toggleHard(i, e.target.checked)}
                />
                {" "}Hard{" "}
                <span
                  className="glyphicon glyphicon-info-sign"
                  data-toggle="tooltip"
                  data-placement="top"
                  title="Optional — check this to get extra practice on this sentence when generating more."
                />
              </label>
              {isSignedIn && (
                <button
                  type="button"
                  className="btn btn-default btn-xs explain-btn"
                  disabled={explainingIds.has(item.id)}
                  onClick={() => toggleExplanation(i)}
                >
                  {explainingIds.has(item.id) ? (
                    <span><span className="glyphicon glyphicon-refresh spin" /> Explaining...</span>
                  ) : (
                    shownExplanations.has(item.id) ? "Hide explanation" : "Explain"
                  )}
                </button>
              )}
              {isSignedIn && (
                <span className="report-wrap">
                  <ReportSentence
                    categories={reportCategories}
                    reportUrl={reportUrl}
                    sentenceId={item.id}
                  />
                </span>
              )}
            </div>
          )}

          {isAnswered && shownExplanations.has(item.id) && explanations[item.id] && (
            <div className="explanation">
              <ExplanationThread
                askUrl={explanationThreadsUrl}
                explanation={explanations[item.id]}
                onAiChecks={syncAiChecks}
                onThreadChange={(thread) => setThreads((prev) => ({ ...prev, [item.id]: thread }))}
                params={{ subject_type: "GrammarExerciseClozeSentence", subject_id: item.id }}
                thread={threads[item.id]}
              />
            </div>
          )}
        </form>
      </div>
    );
  };

  const outOfGenerations = !!generationQuota && generationsUsed >= generationQuota.maxPerMonth;

  const renderBatchComplete = (batch) => {
    const batchItems = batch.indices.map((i) => items[i]);
    const missed = batchItems.filter((item) => item.wasIncorrect).length;
    const hard = batchItems.filter((item) => item.markedHard).length;
    const lastAdd = addedByBatch[batch.batch];
    const addOpen = addOpenForBatch === batch.batch;
    const isLastBatch = batch.batch === lastBatch;

    return (
      <div className="set-complete" key={`complete-${batch.batch}`}>
        <div className="text-center">
          <h2>Set complete! 🎉</h2>
          <p className="lead">
            {batchItems.length} sentences · {missed} missed · {hard} marked hard
          </p>
        </div>

        <div className="panel panel-default">
          <div className="panel-heading">
            {addOpen ? "Pick the sentences to save" : `Worth another look (${batch.flaggedIndices.length})`}
          </div>
          <div className="panel-body">
            {lastAdd && (
              <p className="text-success">
                <span className="glyphicon glyphicon-ok" />
                {" "}Added {lastAdd.count} sentence{lastAdd.count === 1 ? "" : "s"} to {lastAdd.collectionName}!
              </p>
            )}
            {addOpen ? (
              isPro ? (
                <div>
                  {batch.indices.map((i) => (
                    <div className="checkbox" key={items[i].id} style={{ marginTop: 0 }}>
                      <label>
                        <input
                          type="checkbox"
                          checked={selectedForCollection.has(i)}
                          onChange={() => toggleForCollection(i)}
                        />
                        {" "}<ClozeSentence text={items[i].sentence.text} />
                        {" "}
                        {items[i].markedHard && <span className="label label-warning">hard</span>}
                        {" "}
                        {items[i].wasIncorrect && <span className="label label-danger">missed</span>}
                        {" "}
                        {addedIds.has(items[i].id) && <span className="label label-success">added</span>}
                      </label>
                    </div>
                  ))}
                  <CollectionSelector collectionsUrl={collectionsUrl} onSelect={setSelectedCollection} />
                  <button
                    className="btn btn-success joystix"
                    disabled={adding || selectedForCollection.size === 0}
                    onClick={() => addToCollection(batch)}
                    style={{ marginTop: 10 }}
                  >
                    {adding ? "Adding..." : `Add ${selectedForCollection.size} sentence${selectedForCollection.size === 1 ? "" : "s"}`}
                  </button>
                  {" "}
                  <button className="btn btn-link" onClick={() => setAddOpenForBatch(null)}>Cancel</button>
                </div>
              ) : (
                <p className="alert alert-info">Custom collections are a Clozemaster Pro feature.</p>
              )
            ) : (
              <div>
                {batch.flaggedIndices.length > 0 ? batch.flaggedIndices.map((i) => (
                  <p key={items[i].id}>
                    <ClozeSentence text={items[i].sentence.text} />
                    {" "}
                    {items[i].markedHard && <span className="label label-warning">hard</span>}
                    {" "}
                    {items[i].wasIncorrect && <span className="label label-danger">missed</span>}
                    {" "}
                    {addedIds.has(items[i].id) && <span className="label label-success">added</span>}
                  </p>
                )) : (
                  <p className="text-muted">Nothing missed or marked hard — nice work!</p>
                )}
                <button className="btn btn-default" onClick={() => openAddToCollection(batch)}>
                  {lastAdd ? "Add more sentences to a collection" : "Add sentences to a collection"}
                </button>
              </div>
            )}
          </div>
        </div>

        {isLastBatch && (
          <div className="text-center">
            {isSignedIn && (
              generating ? (
                <p className="text-muted">
                  <span className="glyphicon glyphicon-refresh spin" /> Generating more sentences...
                </p>
              ) : (
                <p>
                  <button
                    className="btn btn-success btn-lg joystix"
                    disabled={outOfGenerations}
                    onClick={generateMore}
                  >
                    {batch.flaggedIndices.length > 0 ? "Practice more like these" : "Practice more of these"}
                  </button>
                </p>
              )
            )}
            {isSignedIn && generationQuota && (
              <p className="text-muted quota">
                {outOfGenerations ? (
                  <span>
                    You&apos;ve used all {generationQuota.maxPerMonth.toLocaleString()} of this month&apos;s AI checks —
                    they reset in {generationQuota.monthResetsInDays} days.
                  </span>
                ) : (
                  <span>
                    {generationsUsed.toLocaleString()} / {generationQuota.maxPerMonth.toLocaleString()} AI checks used
                    this month.
                  </span>
                )}
                {" "}Grammar practice is in beta; limits may change.
              </p>
            )}
            {generateError && <p className="text-danger">{generateError}</p>}
            {nextExercise ? (
              <p className="text-muted">
                Up next:{" "}
                <a href={nextExercise.url}>
                  {nextExercise.topicName} · {nextExercise.name}
                  {" "}<span className="glyphicon glyphicon-chevron-right next-chevron" />
                </a>
              </p>
            ) : (
              <p className="text-muted">
                That's the last exercise — <a href={backUrl}>back to topics</a>.
              </p>
            )}
          </div>
        )}
      </div>
    );
  };

  const percentAnswered = items.length ? Math.round((answered.length / items.length) * 100) : 0;

  return (
    <div className="grammar-exercise" ref={containerRef}>
      <div className="header">
        <div className="inner">
          <div className="clearfix">
            <a href={backUrl} className="btn btn-default btn-xs pull-left">
              <span className="glyphicon glyphicon-chevron-left" /> Topics
            </a>
            <button
              className="btn btn-default btn-xs pull-right"
              onClick={() => setShowSettings(!showSettings)}
            >
              <span className="glyphicon glyphicon-cog" />
              <span className="hidden-xs"> Settings</span>
            </button>
            <div className="text-center title">
              <strong>{exercise.name}</strong>
              {" "}
              <span className="text-muted hidden-xs">· {answered.length} / {items.length} answered</span>
              <span className="text-muted visible-xs-inline"> · {answered.length}/{items.length}</span>
            </div>
          </div>
        </div>
        <div className="progress">
          <div
            className="progress-bar progress-bar-success"
            role="progressbar"
            style={{ width: `${percentAnswered}%` }}
            aria-valuenow={percentAnswered}
            aria-valuemin={0}
            aria-valuemax={100}
          >
            <span className="sr-only">{percentAnswered}% answered</span>
          </div>
        </div>

        {/* Inside the sticky header so it opens under the button wherever you're
            scrolled to, rather than off-screen at the top of the page. */}
        {showSettings && (
          <div className="settings">
            <div className="inner">{renderSettings()}</div>
          </div>
        )}
      </div>

      <div className="body">
        <div className="text-center exercise-heading">
          <h2 className="joystix">{exercise.name}</h2>
          <p className="text-muted">
            {exercise.topicName}
            {exercise.description && <span> · {exercise.description}</span>}
          </p>
        </div>

        {exercise.lesson && (
          <div className="panel panel-default lesson">
            <div className="panel-heading">
              <button
                type="button"
                className="btn btn-link lesson-toggle"
                onClick={() => setShowLesson(!showLesson)}
              >
                <span className={`glyphicon glyphicon-chevron-${showLesson ? "down" : "right"}`} />
                {" "}How it works: {exercise.topicName}
              </button>
            </div>
            {showLesson && (
              <div className="panel-body">
                <Markdown>{exercise.lesson}</Markdown>
                {isSignedIn && exercise.topicId && (
                  <ExplanationThread
                    askUrl={explanationThreadsUrl}
                    explanation={null}
                    onAiChecks={syncAiChecks}
                    onThreadChange={setLessonThread}
                    params={{ subject_type: "GrammarTopic", subject_id: exercise.topicId }}
                    thread={lessonThread}
                  />
                )}
              </div>
            )}
          </div>
        )}

        {(exercise.modelSentences || []).length > 0 && (
          <div className="well well-sm model-sentences">
            <strong className="text-muted">{exercise.modelSentences.length === 1 ? "Example" : "Examples"}</strong>
            {exercise.modelSentences.map((text, i) => (
              <p key={i}><ClozeSentence text={text} /></p>
            ))}
          </div>
        )}

        {batches.map((batch) => (
          <div key={`batch-${batch.batch}`}>
            {batch.indices.map((i) => renderItem(items[i], i))}
            {batch.complete && renderBatchComplete(batch)}
          </div>
        ))}
      </div>
    </div>
  );
}
